import {
  createSearchParams,
  generatePath,
  Link,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { pathname } from "#src/config/pathname";
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import {
  Archive,
  ArrowNarrowUpRight,
  Building06,
  Cloud01,
  FileSearch02,
  HardDrive,
} from "@untitled-ui/icons-react";
import { BackupStorageItem } from "#src/components/endpoint/dlp/backupSettings/BackupStorageItem";
import useFetch from "#src/hooks/useFetch";
import dlpService from "#src/services/endpoint/dlp";
import { useMemo, useState } from "react";
import { BackupStorageName } from "#src/services/endpoint/dlp/enums";
import { EndpointDlpS3ConfigModal } from "#src/components/endpoint/dlp/backupSettings/S3ConfigModal";
import { ButtonV2 } from "#src/commonV2/Button";
import { EndpointDlpBackupResetModal } from "#src/components/endpoint/dlp/backupSettings/ResetModal";
import { EndpointDeviceSearchParamKey } from "../devices";

export const DlpBackupSettings = () => {
  const { t } = useTranslation("endpoint", { keyPrefix: "dlp.backupSettings" });

  const { workspaceId } = useParams<"workspaceId">();

  if (!workspaceId) {
    return null;
  }

  // --------------- STATES ---------------

  const [openResetModal, setOpenResetModal] = useState<boolean>(false);
  const [openS3ConfigModal, setOpenS3ConfigModal] = useState<boolean>(false);

  // --------------- COMPUTED ---------------

  const paramWithOnlyWorkspace = useMemo(() => [workspaceId], [workspaceId]);

  // --------------- HOOKS ---------------

  const { result: backupStatistics } = useFetch(
    dlpService.retrieveBackupStatistics,
    paramWithOnlyWorkspace
  );

  const { result: backupSettings, updateData: updateBackupSettings } = useFetch(
    dlpService.retrieveBackupSettings,
    paramWithOnlyWorkspace
  );

  // --------------- METHODS ---------------

  // --------------- EFFECTS ---------------

  // --------------- RENDER ---------------

  return (
    <>
      <div className="pt-5 pb-6 flex flex-col gap-5 min-h-screen">
        {/* Header (Breadcrumb + title) */}
        <div className="px-8 flex flex-col gap-4">
          <BreadcrumbV2
            levels={[
              {
                name: t("breadcrumb.levels.endpoint"),
                path: generatePath(pathname.ENDPOINT_OVERVIEW, { workspaceId }),
              },
            ]}
            currentPage={{
              name: t("breadcrumb.current.dataLossPrevention"),
              tab: t("breadcrumb.current.backupSettings"),
            }}
          />
          <span className="text-display-xs font-semibold text-gray-v2-900">
            {t("title")}
          </span>
        </div>
        {/* Header (Breadcrumb + title) end */}

        {/* Content */}
        <div className="grid grid-cols-[2fr,_1fr] gap-8 px-8 pb-12">
          <div className="flex flex-col gap-5">
            {/* Backed up device count */}
            {backupStatistics === null ||
            backupSettings === null ||
            !backupSettings.storage ? null : (
              <div className="flex items-baseline gap-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-display-lg font-semibold text-gray-v2-400">
                    <span className="text-gray-v2-900">
                      {backupStatistics.enabled}
                    </span>
                    /{backupStatistics.total}
                  </span>
                  <span className="text-lg font-semibold text-gray-v2-900">
                    {t("devices.devicesAreBackedUp")}
                  </span>
                </div>
                <Link
                  to={{
                    pathname: generatePath(pathname.ENDPOINT_DEVICES, {
                      workspaceId,
                    }),
                    search: createSearchParams(
                      backupStatistics.enabled > 0
                        ? {
                            [EndpointDeviceSearchParamKey.BACKUP]: "1",
                          }
                        : {}
                    ).toString(),
                  }}
                  className="flex items-center gap-2 text-lg font-semibold text-brand-v2-700 no-underline"
                >
                  {backupStatistics.enabled > 0
                    ? t("devices.button.viewDevices")
                    : t("devices.button.chooseDevices")}
                  <ArrowNarrowUpRight className="h-6 w-6 min-w-6" />
                </Link>
              </div>
            )}
            {/* Backed up device count end */}

            {/* Backup storage config */}
            <div className="flex flex-col bg-gray-v2-100 rounded-lg shadow-xs">
              <div className="flex justify-between items-center px-4">
                <div className="py-3 flex gap-2 items-center z-10">
                  <Archive className="h-5 w-5 min-w-5 text-gray-v2" />
                  <span className="text-lg font-semibold text-gray-v2-900">
                    {t("backupStorage.title")}
                  </span>
                </div>
                {backupSettings !== null && backupSettings.storage ? (
                  <ButtonV2
                    variant="secondary"
                    size="sm"
                    className="h-fit"
                    onClick={() => {
                      setOpenResetModal(true);
                    }}
                  >
                    {t("backupStorage.button.disableBackup")}
                  </ButtonV2>
                ) : null}
              </div>
              <div
                className={
                  "flex flex-col gap-4 p-4 bg-white border border-gray-v2-200 rounded-lg flex-1"
                }
              >
                <BackupStorageItem
                  enabled={false}
                  Icon={Cloud01}
                  title={t("backupStorage.backblaze.title")}
                  hint={t("backupStorage.backblaze.hint")}
                  comingSoon
                />
                <BackupStorageItem
                  enabled={
                    backupSettings !== null &&
                    backupSettings.storage === BackupStorageName.S3
                  }
                  Icon={Building06}
                  title={t("backupStorage.s3.title")}
                  hint={t("backupStorage.s3.hint")}
                  onClickConfig={() => {
                    setOpenS3ConfigModal(true);
                  }}
                />
                <BackupStorageItem
                  enabled={false}
                  Icon={HardDrive}
                  title={t("backupStorage.googleDrive.title")}
                  hint={t("backupStorage.googleDrive.hint")}
                  comingSoon
                />
              </div>
            </div>
            {/* Backup storage config end */}

            {/* Information */}
            <div className="flex flex-col bg-gray-v2-100 rounded-lg shadow-xs">
              <div className="flex gap-2 px-4 py-3 items-center z-10">
                <FileSearch02 className="h-5 w-5 min-w-5 text-gray-v2" />
                <span className="text-lg font-semibold text-gray-v2-900">
                  {t("information.title")}
                </span>
              </div>
              <div
                className={
                  "flex flex-col gap-4 px-11 py-5 bg-white border border-gray-v2-200 rounded-lg flex-1"
                }
              >
                <div className="flex flex-col gap-1.5">
                  <span className="text-md font-semibold text-gray-v2-900">
                    {t("information.whatGetsBackedUp.title")}
                  </span>
                  <span className="text-md text-gray-v2-600">
                    {t("information.whatGetsBackedUp.hint")}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-md font-semibold text-gray-v2-900">
                    {t("information.supportedFileTypes.title")}
                  </span>
                  <span className="text-md text-gray-v2-600">
                    {t("information.supportedFileTypes.hint")}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-md font-semibold text-gray-v2-900">
                    {t("information.howItWorks.title")}
                  </span>
                  <span className="text-md text-gray-v2-600">
                    {t("information.howItWorks.hint")}
                  </span>
                </div>
              </div>
            </div>
            {/* Information end */}
          </div>
        </div>
        {/* Content end */}
      </div>
      <EndpointDlpS3ConfigModal
        workspaceId={workspaceId}
        open={openS3ConfigModal}
        onClose={() => {
          setOpenS3ConfigModal(false);
        }}
        onRefresh={updateBackupSettings}
        initSettings={backupSettings}
      />
      <EndpointDlpBackupResetModal
        workspaceId={workspaceId}
        open={openResetModal}
        onClose={() => {
          setOpenResetModal(false);
        }}
        onRefresh={updateBackupSettings}
      />
    </>
  );
};
